import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Coins, Globe, Lock, Bot, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

const features = [
  { icon: Zap, title: 'Instant Deploys', desc: 'Your site is live the moment you drop your files.' },
  { icon: Coins, title: 'Earn MGC Coins', desc: 'Play games, earn coins, unlock more hosting — all free.' },
  { icon: Globe, title: 'Free Subdomain', desc: 'Every site gets a free your-site.apexdev.dpdns.org URL.' },
  { icon: Lock, title: 'Auto SSL', desc: 'Automatic HTTPS for every site. No setup needed.' },
  { icon: Bot, title: 'AI Assistant', desc: 'Built-in AI helps you write, fix, and optimize code.' },
  { icon: BarChart3, title: 'Simple Analytics', desc: 'Track page views for every site you deploy.' },
];

const tiers = [
  { name: 'Bronze', price: 'Free', mgc: '0 MGC', projects: '3', storage: '100 MB', features: ['Free subdomain', 'Auto SSL', 'Basic analytics'] },
  { name: 'Silver', price: '500 MGC', mgc: '500 MGC', projects: '10', storage: '1 GB', features: ['Everything in Bronze', 'Custom domains', 'Password protection', 'Priority support'], popular: true },
  { name: 'Gold', price: '2,000 MGC', mgc: '2,000 MGC', projects: '50', storage: '10 GB', features: ['Everything in Silver', 'AI code assistant', 'Serverless functions', 'Team collaboration'] },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-bg">
      {/* Navbar */}
      <nav className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="ApexDev" className="w-7 h-7" />
            <span className="font-bold text-lg">ApexDev</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="btn-ghost text-sm">Sign In</Link>
            <Link to="/signup" className="btn-primary text-sm">Start Deploying</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-32 pb-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-6">
            Deploy static sites<br />
            <span className="bg-gradient-to-r from-accent to-accent-blue bg-clip-text text-transparent">
              in seconds
            </span>
          </h1>
          <p className="text-text-muted text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Free hosting powered by MGC coins. Drop your files and go live instantly.
            No config, no CLI — just your site on the web.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/signup" className="btn-primary text-base px-8 py-3 inline-flex items-center gap-2">
              Start Deploying <ArrowRight size={18} />
            </Link>
            <a href="#features" className="btn-secondary text-base px-8 py-3">Learn More</a>
          </div>
        </motion.div>

        {/* Demo URL */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-12 inline-block bg-surface border border-border rounded-xl px-6 py-4 font-mono text-sm"
        >
          <span className="text-text-muted">https://</span>
          <span className="text-accent">your-site</span>
          <span className="text-text-muted">.apexdev.dpdns.org</span>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-5xl mx-auto px-6 py-24">
        <h2 className="text-3xl font-bold text-center mb-16">
          Why <span className="text-accent">ApexDev?</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="card text-center"
            >
              <f.icon size={32} className="mx-auto mb-4 text-accent" />
              <h3 className="font-semibold mb-2">{f.title}</h3>
              <p className="text-text-muted text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-6 py-24">
        <h2 className="text-3xl font-bold text-center mb-4">Simple Pricing</h2>
        <p className="text-text-muted text-center mb-16">Pay with MGC coins you earn on the platform</p>
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <motion.div
              key={tier.name}
              whileHover={{ scale: 1.02 }}
              className={`card relative ${tier.popular ? 'border-accent/30 ring-1 ring-accent/20' : ''}`}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 badge badge-success text-xs">
                  Most Popular
                </span>
              )}
              <h3 className="text-lg font-bold mb-1">{tier.name}</h3>
              <p className="text-3xl font-extrabold mb-1">{tier.price}</p>
              <p className="text-text-muted text-sm mb-6">{tier.mgc} one-time</p>
              <div className="space-y-2 mb-6">
                <p className="text-sm"><span className="font-semibold">{tier.projects}</span> projects</p>
                <p className="text-sm"><span className="font-semibold">{tier.storage}</span> storage</p>
              </div>
              <ul className="space-y-2 mb-6">
                {tier.features.map(f => (
                  <li key={f} className="text-sm text-text-muted flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-accent" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className={tier.popular ? 'btn-primary w-full text-center block' : 'btn-secondary w-full text-center block'}>
                Get Started
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6 text-center text-text-muted text-sm">
        <div className="space-y-2">
            <p>Built with ❤️ by Tracker Wanga · ApexDev © 2026</p>
            <p className="flex items-center justify-center gap-1.5">
              <span className="text-text-muted">Approved & Supported by</span>
              <a href="https://axislabs.dpdns.org" target="_blank" rel="noopener noreferrer" 
                className="font-bold bg-gradient-to-r from-accent to-accent-blue bg-clip-text text-transparent hover:opacity-80 transition-opacity">
                𝐀𝐱𝐢𝐬 𝐋𝐚𝐛𝐬
              </a>
            </p>
          </div>
      </footer>
    </div>
  );
}
